"use client";

import { useState } from "react";
import { toast } from "sonner";
import { Typography } from "@/components/common/ui/Typography";
import { Dialog } from "@/components/common/ui/Dialog";
import { Button } from "@/components/forms/Button";
import { Input } from "@/components/forms/Input";
import { Textarea } from "@/components/forms/Textarea";
import { useAuthStore } from "@/lib/auth/store";
import { createProblemReport } from "@/lib/reports/api";
import type { FlaggedField } from "@/lib/reports/types";

interface QuoteData {
  totalPremium?: number;
  quoteId?: string;
  effectiveDate?: string;
  expirationDate?: string;
  carrierNotes?: string[];
}

interface CarrierReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: number;
  carrierName: string;
  carrierDisplayName: string;
  coverageType: string;
  quoteData: QuoteData;
  vertical: string;
  insuredName?: string | null;
  // Activity Timeline correlation (LIB-124 / LIB-164). Threaded into the report
  // `context` so support-api can correlate + FK-gate the problem_reported event.
  // Present only for gateway-real (email) submissions — the producer's emit gate.
  taskId?: string | null;
  initialComments?: string;
}

const CARRIER_FIELDS: { key: string; label: string }[] = [
  { key: "total_premium", label: "Total Premium" },
  { key: "quote_id", label: "Quote ID" },
  { key: "effective_date", label: "Effective Date" },
  { key: "expiration_date", label: "Expiration Date" },
  { key: "carrier_notes", label: "Carrier Notes" },
];

function formatFieldValue(key: string, quoteData: QuoteData): string | null {
  switch (key) {
    case "total_premium":
      return quoteData.totalPremium != null
        ? `$${quoteData.totalPremium.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        : null;
    case "quote_id":
      return quoteData.quoteId || null;
    case "effective_date":
      return quoteData.effectiveDate || null;
    case "expiration_date":
      return quoteData.expirationDate || null;
    case "carrier_notes":
      return quoteData.carrierNotes?.length
        ? `${quoteData.carrierNotes.length} note(s)`
        : null;
    default:
      return null;
  }
}

export function CarrierReportModal({
  isOpen,
  onClose,
  applicationId,
  carrierName,
  carrierDisplayName,
  coverageType,
  quoteData,
  vertical,
  insuredName,
  taskId,
  initialComments = "",
}: CarrierReportModalProps) {
  const token = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const [checkedFields, setCheckedFields] = useState<Set<string>>(new Set());
  const [fieldNotes, setFieldNotes] = useState<Record<string, string>>({});
  const [additionalComments, setAdditionalComments] = useState(initialComments);
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setCheckedFields(new Set());
    setFieldNotes({});
    setAdditionalComments("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const toggleField = (key: string) => {
    setCheckedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setFieldNotes((notes) => {
          const updated = { ...notes };
          delete updated[key];
          return updated;
        });
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const updateNote = (key: string, note: string) => {
    setFieldNotes((prev) => ({ ...prev, [key]: note }));
  };

  const handleSubmit = async () => {
    if (!token || !user) return;

    if (checkedFields.size === 0 && !additionalComments.trim()) {
      toast.error("Please select at least one field or add a comment");
      return;
    }

    const flaggedFields: FlaggedField[] = Array.from(checkedFields).map(
      (key) => ({
        key,
        note: fieldNotes[key] || "",
      }),
    );

    setLoading(true);
    try {
      await createProblemReport(token, applicationId, {
        vertical,
        reported_by: user.email,
        flagged_fields: flaggedFields,
        additional_comments: additionalComments,
        report_type: "carrier",
        context: {
          ...(insuredName ? { insured_name: insuredName } : {}),
          // Correlate to the submission's email so support-api emits the
          // problem_reported timeline event (LIB-124 / LIB-164). Omitted for
          // uploads/legacy submissions that carry no gateway task_id.
          ...(taskId ? { task_id: taskId } : {}),
          carrier_name: carrierName,
          carrier_display_name: carrierDisplayName,
          coverage_type: coverageType,
          quote_data: quoteData,
        },
      });
      toast.success("Problem report submitted");
      handleClose();
    } catch {
      toast.error("Failed to submit report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      variant="warning"
      title="Report a Problem"
      subtitle={`${carrierDisplayName} — ${coverageType}`}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit Report"}
          </Button>
        </>
      }
    >
      <div className="space-y-5 max-h-[60vh] overflow-y-auto">
        <div>
          <Typography as="h4" variant="overline" color="light" className="mb-2">
            Quote Fields
          </Typography>
          <div className="space-y-2">
            {CARRIER_FIELDS.map((field) => {
              const value = formatFieldValue(field.key, quoteData);
              return (
                <div key={field.key}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkedFields.has(field.key)}
                      onChange={() => toggleField(field.key)}
                      className="w-4 h-4 rounded border-gray-300 text-navigator-primary focus:ring-navigator-primary/20 cursor-pointer"
                    />
                    <Typography as="span" variant="body" color="secondary">
                      {field.label}
                      {value && (
                        <Typography as="span" variant="body" weight="normal" color="text-gray-400" className="ml-1.5">
                          &mdash; {value}
                        </Typography>
                      )}
                    </Typography>
                  </label>
                  {checkedFields.has(field.key) && (
                    <div className="ml-7 mt-1">
                      <Input
                        inputSize="xs"
                        placeholder="What's wrong with this field?"
                        value={fieldNotes[field.key] || ""}
                        onChange={(e) => updateNote(field.key, e.target.value)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <Typography as="h4" variant="overline" color="light" className="mb-2">
            Additional Comments
          </Typography>
          <Textarea
            value={additionalComments}
            onChange={(e) => setAdditionalComments(e.target.value)}
            placeholder="Describe the issue..."
            rows={3}
          />
        </div>
      </div>
    </Dialog>
  );
}
